import { useState } from 'react';
import { IonActionSheet } from '@ionic/react';
import {
  copyOutline,
  logoWhatsapp,
  shareOutline,
} from 'ionicons/icons';
import { useI18n } from '../i18n/I18nProvider';

type Props = {
  isOpen: boolean;
  url: string;
  shareText: string;
  header: string;
  subHeader?: string;
  onDidDismiss: () => void;
  onCopied?: () => void;
};

/**
 * Compact share sheet: system share, WhatsApp, copy link.
 */
const ShareLinkSheet: React.FC<Props> = ({
  isOpen,
  url,
  shareText,
  header,
  subHeader,
  onDidDismiss,
  onCopied,
}) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      onCopied?.();
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  return (
    <IonActionSheet
      isOpen={isOpen && !!url}
      header={header}
      subHeader={subHeader}
      onDidDismiss={() => {
        setCopied(false);
        onDidDismiss();
      }}
      buttons={[
        ...(typeof navigator !== 'undefined' && typeof navigator.share === 'function'
          ? [
              {
                text: t('shareLink.system'),
                icon: shareOutline,
                handler: () => {
                  void navigator.share({ url, text: shareText }).catch(() => {
                    void copy();
                  });
                },
              },
            ]
          : []),
        {
          text: t('contacts.invite.whatsapp'),
          icon: logoWhatsapp,
          handler: () => {
            window.open(
              `https://wa.me/?text=${encodeURIComponent(shareText)}`,
              '_blank'
            );
          },
        },
        {
          text: copied ? t('contacts.invite.copied') : t('shareLink.copy'),
          icon: copyOutline,
          handler: () => {
            void copy();
            return false;
          },
        },
        {
          text: t('common.actions.cancel'),
          role: 'cancel',
        },
      ]}
    />
  );
};

export default ShareLinkSheet;
